// Outbox do sync — fila local de operações pendentes de PUSH pro servidor.
// Plan: docs/plan/sync-conta-cross-device.md (S3.1).
//
// DB IDB SEPARADO (`iptv-sync`) — NÃO bumpa o DB_VERSION do local-db (cujas
// migrações são delicadas). Desacopla o sync do dado primário: o local-db
// segue sendo a fonte da verdade; o outbox é só o "a enviar".
//
// Cada entrada = 1 item a empurrar (kind/itemKey/payload/updatedAt/deleted) já
// decomposto em (providerCode, profileId). O flusher agrupa por (pc, pid) e
// envia em lote. Dedup por (pc, pid, kind, itemKey): um novo enqueue do MESMO
// item SOBRESCREVE o pendente (mantém só o estado mais recente a enviar — sem
// acumular N posições do mesmo filme durante a reprodução).
//
// ── O DONO de cada entrada (AP-116, gate de cybersec) ──
// Toda entrada nova leva `owner` = a conta Swipe da sessão no momento da ESCRITA, lida
// pela fachada (sync/index.ts) no mesmo tick em que ela confere a sessão e passada pra
// cá. Lida aqui, depois dos `await` do IDB, uma troca de conta no meio carimbava a
// escrita de A com o dono B. É um campo no VALOR (sem índice novo, sem bump do
// DB_VERSION), e entra também no ID: duas contas escrevendo o MESMO item no MESMO perfil
// local (uma conta usando o perfil local da outra, AP-148) têm pendentes separados; com
// o id sem dono, a escrita mais nova de uma apagava o pendente da outra (e trocava o
// dono), e a mais velha caía como `superseded` e se perdia. O flusher só manda e
// só provisiona o perfil de entradas com `owner === conta atual`. Antes, com a sessão de
// A morta sem sign-out (401 no refresh), o outbox de A ficava; B pareava e o flush de B
// CRIAVA na conta de B o perfil "Perfil da A" com o histórico de A (provado em PGlite
// pelo cybersec). Entrada SEM dono (de antes desta versão) nunca provisiona e só sai se
// o perfil já for da conta da sessão (`GET /profiles`); fora disso é descartada (ver o
// veredito no cabeçalho do flusher). Adotar entrada sem dono como da sessão atual é
// inseguro.
//
// A troca de conta NÃO apaga entrada (ver `loginSwipeSession`): a de outra conta fica com
// o dono dela e sai quando ela voltar. Só o sign-out esvazia o outbox.

import type { DBSchema, IDBPDatabase } from 'idb'

import { makeDbOpener } from '../idb-opener'

import { currentSyncBoot } from './boot-count'
import type { SyncKind } from './types'

export interface OutboxEntry {
  // key = `${providerCode}::${profileId}::${kind}::${itemKey}::o:${owner}` (dedup por item
  // E dono; a entrada sem dono, de antes do dono existir, não tem o sufixo)
  id: string
  providerCode: string
  profileId: string
  kind: SyncKind
  itemKey: string
  payload: unknown
  updatedAt: string // ISO (LWW)
  deleted: boolean
  enqueuedAt: number // unix ms (ordenação/observabilidade; e o prazo da entrada de outra conta)
  /** O boot (execução do app) da escrita: o prazo da entrada de outra conta conta boots
   *  além de tempo (sync/boot-count.ts). Ausente sem localStorage. */
  enqueuedBoot?: number
  /** Quem enfileirou, quando importa pra observabilidade (ex.: o tombstone da
   *  migração de carimbos, cujo desfecho no servidor vai pra telemetria). Nunca vai no
   *  corpo do push. */
  origin?: OutboxOrigin
  /** A conta Swipe (`swipeUserId`) da sessão que enfileirou. Ausente = entrada de antes
   *  desta versão (ver "O DONO" no cabeçalho). Nunca vai no corpo do push. */
  owner?: string
  /** Presente = a entrada está em QUARENTENA (ver "QUARENTENA" abaixo): o servidor a
   *  recusou de forma terminal, ou ela nunca passaria na validação dele. Fica guardada
   *  pra diagnóstico e NÃO sai mais no flush. Nunca vai no corpo do push. */
  quarantined?: QuarantineMark
}

/** Origens rastreadas do outbox (ver `OutboxEntry.origin`). */
export type OutboxOrigin = 'stamp-migration'

// ── QUARENTENA (item envenenado, 10/10/2026) ──
// Um item que o servidor recusa por CONTEÚDO (400 do zod: payload acima de 16 KB,
// itemKey fora do tamanho) recebia a mesma resposta pra sempre: o `pushSync` virava
// `null`, o flush abortava inteiro e o MESMO lote voltava no flush seguinte. Um único
// item travava o push do aparelho todo, calado.
//
// A quarentena tira a entrada da fila de envio SEM apagá-la: ela fica no MESMO store,
// com esta marca, e o flusher não a manda mais (o `drainGroups` a ENTREGA, marcada, porque o
// prazo da entrada de outra conta vale pra ela também). Sem object store novo de propósito (bump de
// versão de IDB é o tipo de migração que o fleet Tizen/webOS paga caro). O dado local
// do usuário (local-db) não é tocado; e a próxima escrita do MESMO item SOBRESCREVE a
// entrada marcada (o `enqueue` grava um registro novo, sem a marca), então um item que
// voltou a ser válido volta a subir sozinho.
//
// Teto: `QUARANTINE_MAX` entradas marcadas. Passou, saem as marcadas mais antigas (é
// diagnóstico, não fila). O sign-out (`clearOutbox`) leva tudo junto, como o resto do
// outbox da conta.

/** O que se sabe da recusa, pra diagnóstico. Sem payload copiado: ele já está na entrada. */
export interface QuarantineMark {
  /** Quando entrou em quarentena (unix ms). */
  at: number
  /** Status HTTP da recusa, ou `null` quando a validação LOCAL já reprovou (sem pedido). */
  status: number | null
  /** Motivo curto: o `error` do corpo do servidor, ou o motivo local. */
  reason: string
}

/** Teto de entradas em quarentena guardadas (as mais antigas saem primeiro). */
export const QUARANTINE_MAX = 100

interface OutboxDb extends DBSchema {
  outbox: {
    key: string
    value: OutboxEntry
    indexes: { 'by-scope': [string, string] } // [providerCode, profileId]
  }
}

/**
 * ⚠️ Este banco é a FILA que leva progresso e favoritos pro servidor. Antes do
 * abridor único, uma rejeição de `openDB` ficava cacheada em `_db` e a fila
 * morria pela sessão inteira — o sync cross-device parava SEM UM ÚNICO SINAL, e
 * o dado seguia salvando local e nunca subindo. É o pior dos cinco bancos pra
 * ter esse defeito, porque o sintoma (o favorito não aparece no outro aparelho)
 * não se parece em nada com a causa.
 *
 * O `makeDbOpener` dá soltura da promise, guarda de identidade, janela de
 * reabertura e registro no "Limpar dados" — por construção, não por cópia.
 */
export const outboxDb = makeDbOpener<OutboxDb>({
  name: 'iptv-sync',
  version: 1,
  upgrade(db) {
    const s = db.createObjectStore('outbox', { keyPath: 'id' })
    s.createIndex('by-scope', ['providerCode', 'profileId'])
  },
})

const getDb = (): Promise<IDBPDatabase<OutboxDb>> => outboxDb.get()

function entryId(
  providerCode: string,
  profileId: string,
  kind: string,
  itemKey: string,
  owner: string | undefined,
): string {
  const base = `${providerCode}::${profileId}::${kind}::${itemKey}`
  return owner === undefined ? base : `${base}::o:${owner}`
}

export interface EnqueueInput {
  providerCode: string
  profileId: string
  kind: SyncKind
  itemKey: string
  payload?: unknown
  updatedAt: string
  deleted?: boolean
  origin?: OutboxOrigin
  /** A conta Swipe da escrita (ver "O DONO"). A fachada sempre passa; ausente só em
   *  teste (a entrada nasce como as de antes do dono, e fica em quarentena). */
  owner?: string
}

/**
 * Desfecho de um `enqueue`:
 * - `queued`: gravado agora;
 * - `superseded`: já havia um pendente do MESMO item igual ou mais novo, e ele fica
 *   (LWW local: não retrocede). O item segue na fila, mas é o pendente que vai, não
 *   este. Pra um tombstone ancorado no passado é o comportamento certo (uma escrita
 *   legítima posterior vence), e quem precisa saber lê isto;
 * - `failed`: o write foi DESCARTADO (quota/IDB).
 */
export type EnqueueOutcome = 'queued' | 'superseded' | 'failed'

/**
 * Enfileira (ou sobrescreve) um item no outbox. Dedup por (pc,pid,kind,itemKey):
 * se já há um pendente do mesmo item, o NOVO substitui SE for mais recente (LWW
 * local — evita reenviar uma posição velha por cima de uma nova ainda não
 * enviada). Best-effort: erro de IDB não pode quebrar o write primário (o caller
 * já gravou no local-db); engole e loga.
 *
 * Devolve o desfecho (`EnqueueOutcome`). Quem precisa de garantia — a migração de
 * limpeza, cujo tombstone descartado deixaria o servidor devolver o dado apagado — lê
 * isto e tenta de novo depois. Os callers que ignoram o retorno seguem com o
 * comportamento de antes.
 */
export async function enqueue(input: EnqueueInput): Promise<EnqueueOutcome> {
  try {
    const db = await getDb()
    const owner = input.owner
    const boot = currentSyncBoot()
    const id = entryId(input.providerCode, input.profileId, input.kind, input.itemKey, owner)
    const existing = await db.get('outbox', id)
    if (existing && existing.updatedAt >= input.updatedAt) {
      return 'superseded' // pendente já é igual ou mais novo → não retrocede (e o item segue na fila)
    }
    const entry: OutboxEntry = {
      id,
      providerCode: input.providerCode,
      profileId: input.profileId,
      kind: input.kind,
      itemKey: input.itemKey,
      payload: input.payload ?? null,
      updatedAt: input.updatedAt,
      deleted: input.deleted ?? false,
      enqueuedAt: Date.now(),
      ...(input.origin !== undefined ? { origin: input.origin } : {}),
      ...(owner !== undefined ? { owner } : {}),
      ...(boot !== null ? { enqueuedBoot: boot } : {}),
    }
    await db.put('outbox', entry)
    return 'queued'
  } catch (err) {
    console.warn('[sync] enqueue falhou (best-effort):', err)
    return 'failed'
  }
}

/** Quantos itens pendentes há (pra decidir se vale flush / observabilidade). A entrada
 *  em quarentena não é pendente: ela não sai mais. */
export async function pendingCount(): Promise<number> {
  try {
    const db = await getDb()
    const all = await db.getAll('outbox')
    return all.filter((e) => e.quarantined === undefined).length
  } catch {
    return 0
  }
}

/**
 * Todas as entradas, agrupadas por (providerCode, profileId) — INCLUSIVE as em
 * quarentena, que o flusher não manda mas precisa ver: o prazo da entrada de OUTRA conta
 * vale pra elas também (cybersec P2: pulá-las aqui deixava a quarentena alheia no
 * aparelho pra sempre). Quem lê decide pelo campo `quarantined`.
 */
export async function drainGroups(): Promise<
  Array<{ providerCode: string; profileId: string; entries: OutboxEntry[] }>
> {
  try {
    const db = await getDb()
    const all = await db.getAll('outbox')
    const groups = new Map<string, { providerCode: string; profileId: string; entries: OutboxEntry[] }>()
    for (const e of all) {
      const k = `${e.providerCode}::${e.profileId}`
      let g = groups.get(k)
      if (!g) {
        g = { providerCode: e.providerCode, profileId: e.profileId, entries: [] }
        groups.set(k, g)
      }
      g.entries.push(e)
    }
    return [...groups.values()]
  } catch {
    return []
  }
}

/**
 * Remove do outbox os itens JÁ ENVIADOS com sucesso. Só remove se o `updatedAt`
 * no outbox ainda for o mesmo enviado — se um write novo chegou DURANTE o flush
 * (updatedAt mudou), preserva o pendente novo (não perde a atualização). Isso
 * evita a corrida "flush envia v1, user gera v2, flush apaga e v2 some".
 */
export async function ackSent(
  sent: Array<{ id: string; updatedAt: string }>,
): Promise<void> {
  await deleteIfUnchanged(sent)
}

/** O delete condicional do ack e do descarte. Devolve se a transação fechou. */
async function deleteIfUnchanged(sent: Array<{ id: string; updatedAt: string }>): Promise<boolean> {
  if (sent.length === 0) return true
  try {
    const db = await getDb()
    const tx = db.transaction('outbox', 'readwrite')
    await Promise.all(
      sent.map(async ({ id, updatedAt }) => {
        const cur = await tx.store.get(id)
        if (cur && cur.updatedAt === updatedAt) {
          await tx.store.delete(id)
        }
      }),
    )
    await tx.done
    return true
  } catch (err) {
    console.warn('[sync] ackSent falhou (best-effort):', err)
    return false
  }
}

/**
 * Descarta entradas do outbox SEM terem sido enviadas (a entrada sem dono cujo perfil
 * não é da conta, ver o flusher). Mesma regra do `ackSent`: só sai quem não mudou desde
 * a leitura (uma escrita nova do mesmo item, no meio, fica). Devolve se o IDB aceitou.
 */
export async function discardIfUnchanged(entries: Array<{ id: string; updatedAt: string }>): Promise<boolean> {
  return deleteIfUnchanged(entries)
}

/**
 * Põe entradas em QUARENTENA (ver o bloco "QUARENTENA"): marca, sem apagar, só as que
 * não mudaram desde a leitura (mesma regra do `ackSent`: uma escrita nova do mesmo
 * item, no meio do flush, fica pendente e é tentada de novo). Depois aplica o teto
 * `QUARANTINE_MAX`. Devolve quantas foram marcadas (`0` também quando o IDB falhou: a
 * entrada segue pendente e o flush seguinte a isola de novo).
 */
export async function quarantineIfUnchanged(
  entries: Array<{ id: string; updatedAt: string }>,
  mark: QuarantineMark,
): Promise<number> {
  if (entries.length === 0) return 0
  try {
    const db = await getDb()
    const tx = db.transaction('outbox', 'readwrite')
    let marked = 0
    await Promise.all(
      entries.map(async ({ id, updatedAt }) => {
        const cur = await tx.store.get(id)
        if (cur && cur.updatedAt === updatedAt && cur.quarantined === undefined) {
          await tx.store.put({ ...cur, quarantined: mark })
          marked += 1
        }
      }),
    )
    await tx.done
    await trimQuarantine()
    return marked
  } catch (err) {
    console.warn('[sync] quarentena falhou (best-effort):', err)
    return 0
  }
}

/** Aplica o teto da quarentena: apaga as marcadas mais antigas além de `QUARANTINE_MAX`. */
async function trimQuarantine(): Promise<void> {
  const db = await getDb()
  const all = await db.getAll('outbox')
  const marked = all
    .filter((e): e is OutboxEntry & { quarantined: QuarantineMark } => e.quarantined !== undefined)
    .sort((a, b) => a.quarantined.at - b.quarantined.at)
  const excess = marked.length - QUARANTINE_MAX
  if (excess <= 0) return
  const tx = db.transaction('outbox', 'readwrite')
  await Promise.all(marked.slice(0, excess).map((e) => tx.store.delete(e.id)))
  await tx.done
}

/** As entradas em quarentena DE UMA CONTA (diagnóstico: Ajustes, suporte, console). O
 *  dono é obrigatório (cybersec P2): numa TV compartilhada, a quarentena de outra conta
 *  não é desta pra ler. */
export async function listQuarantined(owner: string): Promise<OutboxEntry[]> {
  try {
    const db = await getDb()
    const all = await db.getAll('outbox')
    return all.filter((e) => e.quarantined !== undefined && e.owner === owner)
  } catch {
    return []
  }
}

/** Limpa o outbox inteiro (logout / reset). */
export async function clearOutbox(): Promise<void> {
  try {
    const db = await getDb()
    await db.clear('outbox')
  } catch {
    // ignora
  }
}
